import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import {
  LogOut, School as SchoolIcon, MessageCircle, BarChart3, Inbox, Clock,
} from 'lucide-react';

/**
 * School Parent Home — Ticket 1A.7.
 * Branded read-only observer dashboard. No PIN, no Family Home, no edit.
 */
export default function SchoolParentHome() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [school, setSchool] = useState(null);
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const meRes = await api.get('/auth/me');
      setMe(meRes.data);
      const calls = [api.get('/children')];
      if (meRes.data?.school_id) {
        calls.push(api.get(`/schools/${meRes.data.school_id}`));
      }
      const [kidsRes, schoolRes] = await Promise.all(calls);
      // Show only school-linked children for the school-parent observer view
      const linked = (kidsRes.data || []).filter(
        (c) => c.linked_parent_id === meRes.data.user_id,
      );
      setChildren(linked);
      if (schoolRes) setSchool(schoolRes.data);
    } catch {
      /* route guard handles auth */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleLogout = async () => {
    try { await api.post('/auth/logout'); } catch { /* ignore */ }
    localStorage.removeItem('yukti_session_token');
    const slug = school?.slug;
    navigate(slug ? `/school/${slug}/login` : '/', { replace: true });
  };

  const brandColor = school?.brand_color || '#4F46E5';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div
          className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600"
          data-testid="school-parent-boot-spinner"
        />
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50"
      data-testid="school-parent-home"
    >
      <header
        className="border-b bg-white/90 backdrop-blur sticky top-0 z-10"
        style={{ borderTopWidth: 4, borderTopColor: brandColor }}
      >
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {school?.logo_url ? (
              <img
                src={school.logo_url} alt={school.name}
                className="w-10 h-10 rounded-lg object-contain bg-white border"
                data-testid="school-parent-logo"
              />
            ) : (
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center text-white"
                style={{ backgroundColor: brandColor }}
                data-testid="school-parent-logo-fallback"
              >
                <SchoolIcon size={20} />
              </div>
            )}
            <div>
              <h1
                className="font-bold text-slate-900"
                data-testid="school-parent-school-name"
              >
                {school?.name || 'Your School'}
              </h1>
              <p className="text-xs text-slate-500">
                <span data-testid="school-parent-email">{me?.email}</span>
                <span> · Parent Observer</span>
              </p>
            </div>
          </div>
          <Button
            variant="ghost" size="sm"
            onClick={handleLogout}
            data-testid="school-parent-logout-btn"
          >
            <LogOut size={16} className="mr-1.5" /> Logout
          </Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        <div className="mb-6">
          <h2
            className="text-2xl font-bold text-slate-900"
            data-testid="school-parent-welcome"
          >
            Welcome 👋
          </h2>
          <p className="text-sm text-slate-600 mt-1">
            Read-only access to your child's learning conversations and progress.
          </p>
        </div>

        {children.length === 0 ? (
          <EmptyState />
        ) : (
          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
            data-testid="observed-children-grid"
          >
            {children.map((c) => (
              <ChildCard
                key={c.child_id}
                child={c}
                brandColor={brandColor}
                onChats={() => navigate(`/parent/child/${c.child_id}/chats`)}
                onAnalytics={() => navigate(`/insights/${c.child_id}`)}
                onAssessments={() => navigate(`/school-parent/child/${c.child_id}/assessments`)}
              />
            ))}
          </div>
        )}

        <p className="text-xs text-slate-400 mt-12">
          Need help? Contact your school admin.
        </p>
      </main>
    </div>
  );
}

function EmptyState() {
  return (
    <div
      className="border-2 border-dashed border-slate-200 rounded-2xl py-16 px-6 text-center bg-white/60"
      data-testid="school-parent-empty-state"
    >
      <div className="mx-auto w-14 h-14 rounded-full bg-indigo-50 flex items-center justify-center mb-4">
        <Inbox size={26} className="text-indigo-500" />
      </div>
      <p className="text-slate-900 font-semibold text-lg">No child linked yet</p>
      <p className="text-sm text-slate-600 mt-1 max-w-md mx-auto">
        Your school will link a child to your account once enrollment is processed.
        You'll receive an email once they do.
      </p>
    </div>
  );
}

function ChildCard({ child, brandColor, onChats, onAnalytics, onAssessments }) {
  return (
    <div
      className="bg-white border rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow flex flex-col"
      data-testid={`observed-child-card-${child.child_id}`}
    >
      <div className="flex items-center gap-3 mb-3">
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold"
          style={{ backgroundColor: brandColor }}
        >
          {(child.name || '?').charAt(0).toUpperCase()}
        </div>
        <div>
          <div className="font-bold text-slate-900">{child.name}</div>
          <div className="text-xs text-slate-500">
            {child.grade ? `Grade ${child.grade}` : 'Student'}
          </div>
        </div>
      </div>
      {child.last_active && (
        <div className="text-xs text-slate-500 flex items-center gap-1 mb-4">
          <Clock size={12} />
          Last active {new Date(child.last_active).toLocaleDateString()}
        </div>
      )}
      <div className="space-y-2 mt-auto">
        <Button
          variant="outline" size="sm"
          className="w-full justify-start"
          onClick={onChats}
          data-testid={`view-child-chats-btn-${child.child_id}`}
        >
          <MessageCircle size={14} className="mr-2" />
          View Conversations
        </Button>
        <Button
          variant="outline" size="sm"
          className="w-full justify-start"
          onClick={onAnalytics}
          data-testid={`view-child-analytics-btn-${child.child_id}`}
        >
          <BarChart3 size={14} className="mr-2" />
          View Analytics
        </Button>
        <Button
          variant="outline" size="sm"
          className="w-full justify-start"
          onClick={onAssessments}
          data-testid={`view-child-assessments-btn-${child.child_id}`}
        >
          <span className="w-4 h-4 mr-2 inline-flex items-center justify-center font-bold text-[11px] rounded" style={{ backgroundColor: brandColor, color: 'white' }}>A</span>
          View Assessments
        </Button>
      </div>
    </div>
  );
}
